import React, { useMemo, useState, useEffect } from "react";
import { Dialog, DialogContent } from "@shared/ui/dialog";
import { PageHeader } from "@shared/components/PageHeader";
import { Button } from "@shared/ui/button";
import { Upload, X, FilePlus, FileText } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@shared/ui/select";
import { Input } from "@shared/ui/input";
import { useDocumentType, useDocumentSubtype, useUploadDocumentsForConsumer } from "@features/cx/consumerManagement/hooks";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { toast } from "@shared/ui/use-toast";

interface UploadDocumentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: (data: any) => void;
}

export const UploadDocumentModal: React.FC<UploadDocumentModalProps> = ({
  isOpen,
  onClose,
  onUpload,
}) => {
  const remoteUtilityId = getRemoteUtilityId();
  const [selectedDocType, setSelectedDocType] = useState<string>("");
  const [selectedSubtype, setSelectedSubtype] = useState<string>("");
  const [referenceNumber, setReferenceNumber] = useState<string>("");
  const [file, setFile] = useState<File | null>(null);
  const [dragging, setDragging] = useState(false);

  const uploadMutation = useUploadDocumentsForConsumer();

  const { data: documentTypeData, isLoading: isDocumentTypeLoading } = useDocumentType({
    remote_utility_id: remoteUtilityId,
    config_level: "document_type",
  });

  const documentTypeOptions = useMemo(() => {
    return (
      documentTypeData?.result?.map((type: any) => ({
          label: type.name,
          value: type.name,
        })) || []
    );
  }, [documentTypeData]);

  const selectedTypeCode = useMemo(() => {
    if (!selectedDocType || !documentTypeData?.result) return "";
    const type = documentTypeData.result.find((t: any) => t.name === selectedDocType);
    return type?.code || "";
  }, [selectedDocType, documentTypeData]);

  const { data: documentSubtypeData, isLoading: isDocumentSubtypeLoading } = useDocumentSubtype(
    {
      remote_utility_id: remoteUtilityId,
      config_level: "document_subtype",
      code_list: selectedTypeCode,
    },
    {
      enabled: !!selectedTypeCode,
    }
  );

  const subTypeOptions = useMemo(() => {
    if (!documentSubtypeData?.result || !selectedTypeCode) return [];
    const typePrefix = selectedTypeCode.split("#")[0];
    return documentSubtypeData.result
      .filter((subtype: any) => subtype.isActive && (subtype.code?.includes(typePrefix) || true))
      .map((subtype: any) => ({
        label: subtype.name,
        value: subtype.name,
      }));
  }, [documentSubtypeData, selectedTypeCode]);

  const handleCancel = () => {
    setSelectedDocType("");
    setSelectedSubtype("");
    setReferenceNumber("");
    setFile(null);
    setDragging(false);
    onClose();
  };

  const handleUpload = () => {
    if (!selectedDocType || !selectedSubtype || !file) {
      toast({
        title: "Error",
        description: "Please fill all fields and select a file.",
        variant: "destructive",
      });
      return;
    }
    
    // Pass back data in the structure the parent component expects
    onUpload({
      documentType: selectedDocType,
      subType: selectedSubtype,
      referenceNumber,
      documentFile: file,
    });
    
    handleCancel();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleCancel}>
      <DialogContent className="max-w-2xl bg-white rounded-lg shadow-xl border p-0 overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b border-gray-100 flex-shrink-0">
          <PageHeader
            title="Upload Document"
            subtitle="Select document type, sub type, and upload the file."
          />
        </div>

        {/* Body */}
        <div className="px-6 py-6 space-y-6 overflow-y-auto flex-1">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Document Type Dropdown */}
            <div>
              <label className="text-sm font-medium mb-2 block">
                Select Document Type <span className="text-red-500">*</span>
              </label>
              <Select
                value={selectedDocType}
                onValueChange={(val) => {
                  setSelectedDocType(val);
                  setSelectedSubtype(""); // reset subtype
                }}
                disabled={isDocumentTypeLoading}
              >
                <SelectTrigger>
                  <SelectValue placeholder={isDocumentTypeLoading ? "Loading..." : "Choose document type..."} />
                </SelectTrigger>
                <SelectContent>
                  {documentTypeOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Document Sub-Type Dropdown */}
            <div>
              <label className="text-sm font-medium mb-2 block">
                Select Document Sub-Type <span className="text-red-500">*</span>
              </label>
              <Select
                value={selectedSubtype}
                onValueChange={setSelectedSubtype}
                disabled={!selectedDocType || isDocumentSubtypeLoading}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={
                      !selectedDocType
                        ? "Select document type first..."
                        : isDocumentSubtypeLoading
                        ? "Loading..."
                        : "Choose document sub-type..."
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {subTypeOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Reference Number */}
            <div>
              <label className="text-sm font-medium mb-2 block">
                Reference Number (optional)
              </label>
              <Input
                value={referenceNumber}
                onChange={(e) => setReferenceNumber(e.target.value)}
                placeholder="Enter reference number"
              />
            </div>
          </div>

          {/* Upload Area */}
          <div>
            <label className="text-sm font-medium mb-2 block">
              Upload File <span className="text-red-500">*</span>
            </label>
            {!file ? (
              <div
                className={`flex flex-col items-center justify-center min-h-[160px] text-center border-2 border-dashed rounded-md p-6 transition-colors ${
                  dragging ? "bg-blue-50 border-blue-400" : "border-gray-300 hover:border-gray-400"
                } ${
                  (!selectedDocType || !selectedSubtype) ? "opacity-50 pointer-events-none" : "cursor-pointer"
                }`}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragging(true);
                }}
                onDragLeave={() => setDragging(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragging(false);
                  if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
                    const droppedFile = e.dataTransfer.files[0];
                    const allowedTypes = ["image/jpeg", "image/png", "application/pdf"];
                    if (!allowedTypes.includes(droppedFile.type)) {
                      toast({
                        title: "Invalid file type",
                        description: "Only JPG, PNG, and PDF files are allowed.",
                        variant: "destructive",
                      });
                      return;
                    }
                    setFile(droppedFile);
                  }
                }}
                onClick={() => {
                  document.getElementById("modal-file-upload")?.click();
                }}
              >
                <Upload className="h-8 w-8 text-gray-400 mb-3" />
                <h4 className="text-sm font-medium text-gray-700 mb-1">
                  Drag and drop or click to upload
                </h4>
                <p className="text-xs text-gray-500 mb-4">
                  Maximum file size: 5MB. Accepted formats: JPG, PNG, PDF.
                </p>
                <input
                  type="file"
                  id="modal-file-upload"
                  className="hidden"
                  accept=".jpg,.jpeg,.png,.pdf"
                  onChange={(e) => {
                    if (e.target.files && e.target.files.length > 0) {
                      const selectedFile = e.target.files[0];
                      const allowedTypes = ["image/jpeg", "image/png", "application/pdf"];
                      if (!allowedTypes.includes(selectedFile.type)) {
                        toast({
                          title: "Invalid file type",
                          description: "Only JPG, PNG, and PDF files are allowed.",
                          variant: "destructive",
                        });
                        e.target.value = "";
                        return;
                      }
                      setFile(selectedFile);
                    }
                    e.target.value = "";
                  }}
                  onClick={(e) => e.stopPropagation()}
                />
                <Button size="sm" variant="outline" className="pointer-events-none">
                  <FilePlus className="h-4 w-4 mr-2" />
                  Browse Files
                </Button>
              </div>
            ) : (
              <div className="border border-gray-200 rounded-md p-4 bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <FileText className="h-6 w-6 mr-3 text-blue-500" />
                    <div>
                      <span className="text-sm font-medium text-gray-900 block max-w-[300px] truncate">
                        {file.name}
                      </span>
                      <span className="text-xs text-gray-500">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </span>
                    </div>
                  </div>
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => setFile(null)}
                    className="h-8 w-8 text-gray-500 hover:text-red-500 bg-white shadow-sm border border-gray-200"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-end gap-3 flex-shrink-0">
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleUpload}
            className="px-6 h-9 text-sm bg-blue-600 hover:bg-blue-700 text-white font-semibold"
          >
            Upload
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
